/**
 * Record a transfer made elsewhere. Amount must equal `payableMinor` unless override+reason.
 * TMMPDM-0037: 409 `PAYOUT_NOT_PAYABLE` (a CHARGED settlement, tmm-api 0099) says why in words.
 * TMMPDM-0044: Override covers an amount mismatch only ("Record a different amount"), so it
 * starts unticked. tmm-api refuses `HOLD_TAX_DOCS`, `HOLD_RESELLER_UNPAID` and
 * `HOLD_AFFILIATE_REVIEW` before it reads `override`: `RecordPayoutAction` disables Record payout
 * on those with the reason, another hold
 * gets an info line, and a 409 `PAYOUT_ON_HOLD` / `PAYOUT_NOT_PAYABLE` closes the dialog through
 * `onRefused`, whose caller shows the message and re-reads its list.
 * TMMPDM-0095: from the ledger's `LIVE` a stale page's record answers 410 `PAYOUTS_MOVED_TO_LEDGER`:
 * the dialog says so with the payee's Ledger link and `onMovedToLedger` re-reads.
 */
import { useId, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { adminApi } from '@/api/tmm/admin';
import { majorFromMinor, majorToMinor } from '@/format';
import { AppSwitch } from '@/components/ui/AppSwitch';
import type { PayoutHold, PayoutRequest, PayoutStatementView } from '@/api/tmm/types';
import { isPayoutsMovedToLedger } from '@/api/errors';
import { isPayoutNotPayable, isPayoutOnHold, isRefusedHold, refusedHoldOf } from './payoutError';
import { MovedToLedgerMessage } from '../ledger/ledgerShared';
import { affiliateLedgerPath, resellerLedgerPath } from '../ledger/ledger';

interface RecordPayoutDialogProps {
  open: boolean;
  row: PayoutStatementView | null;
  onClose: () => void;
  onRecorded: (updated: PayoutStatementView) => void;
  /** The server refused the payout: the caller closes the dialog, shows `message`, re-reads. */
  onRefused: (message: string) => void;
  /** 410 `PAYOUTS_MOVED_TO_LEDGER`: the dialog stays open saying so; the caller re-reads. */
  onMovedToLedger?: (() => void) | undefined;
}

const HOLD_FALLBACK: Record<PayoutHold, string> = {
  HOLD_TAX_DOCS: 'Tax docs',
  HOLD_STRIPE_CONNECT: 'Stripe Connect',
  HOLD_TRANSFER_CURRENCY: 'Transfer currency',
  HOLD_TRANSFER_CAP: 'Transfer cap',
  HOLD_RESELLER_UNPAID: 'Reseller charge unpaid',
  HOLD_AFFILIATE_REVIEW: 'Held for review (affiliate ended for cause)',
};

function todayIso(today: Date = new Date()): string {
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function toDateTime(date: string): string {
  return date.includes('T') ? date : `${date}T00:00:00.000Z`;
}

interface RecordPayoutActionProps {
  row: PayoutStatementView;
  onRecord: (row: PayoutStatementView) => void;
  /** An accessible name that says which row, where the table needs one. */
  ariaLabel?: string | undefined;
}

/**
 * A PENDING statement's Record payout…: disabled with its reason on a hold the server refuses
 * (PERM-003 / ACT-006), keyed off the server's hold code alone.
 */
export function RecordPayoutAction({ row, onRecord, ariaLabel }: RecordPayoutActionProps) {
  const { t } = useCopy();
  const reasonId = useId();
  const label = t('payouts.record.action', 'Record payout…');
  const named = ariaLabel !== undefined ? { 'aria-label': ariaLabel } : {};
  if (!isRefusedHold(row.hold)) {
    return (
      <Button size="small" onClick={() => onRecord(row)} {...named}>
        {label}
      </Button>
    );
  }
  return (
    <Stack spacing={0.25} sx={{ alignItems: 'flex-start' }}>
      <Button size="small" disabled aria-describedby={reasonId} {...named}>
        {label}
      </Button>
      <Typography id={reasonId} variant="caption" color="text.secondary">
        {t('payouts.record.refusedHold', "Can't be recorded while {{hold}}.", {
          hold: t(`payouts.hold.${row.hold}`, HOLD_FALLBACK[row.hold]),
        })}
      </Typography>
    </Stack>
  );
}

export function RecordPayoutDialog({
  open,
  row,
  onClose,
  onRecorded,
  onRefused,
  onMovedToLedger,
}: RecordPayoutDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {open && row ? (
        <RecordPayoutForm
          key={row.id}
          row={row}
          onClose={onClose}
          onRecorded={onRecorded}
          onRefused={onRefused}
          onMovedToLedger={onMovedToLedger}
        />
      ) : null}
    </Dialog>
  );
}

function RecordPayoutForm({
  row,
  onClose,
  onRecorded,
  onRefused,
  onMovedToLedger,
}: {
  row: PayoutStatementView;
  onClose: () => void;
  onRecorded: (updated: PayoutStatementView) => void;
  onRefused: (message: string) => void;
  onMovedToLedger?: (() => void) | undefined;
}) {
  const { t } = useCopy();
  const holdLabel = (hold: PayoutHold) => t(`payouts.hold.${hold}`, HOLD_FALLBACK[hold]);
  const [amount, setAmount] = useState(majorFromMinor(row.payableMinor, row.currency));
  const [paidAt, setPaidAt] = useState(todayIso());
  const [ref, setRef] = useState('');
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<ReactNode>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const submit = async () => {
    const nextFields: Record<string, string> = {};
    const trimmedRef = ref.trim();
    if (!trimmedRef) {
      nextFields.ref = t('payouts.record.refRequired', 'Reference is required.');
    }
    if (override && reason.trim() === '') {
      nextFields.reason = t('payouts.record.reasonRequired', 'Reason is required.');
    }
    if (Object.keys(nextFields).length > 0) {
      setFieldErrors(nextFields);
      return;
    }
    const body: PayoutRequest = {
      amountMinor: majorToMinor(amount, row.currency),
      paidAt: toDateTime(paidAt),
      ref: trimmedRef,
      ...(override ? { override: true, reason: reason.trim() } : {}),
    };
    setSubmitting(true);
    setFormError(null);
    try {
      const updated = await adminApi.recordPayout(row.tenantSlug, row.id, body);
      onRecorded(updated);
    } catch (error: unknown) {
      if (isPayoutsMovedToLedger(error)) {
        setFormError(
          <MovedToLedgerMessage
            ledgerTo={
              row.payeeKind === 'RESELLER'
                ? resellerLedgerPath(row.payeeId)
                : affiliateLedgerPath(row.tenantSlug, row.payeeId)
            }
          />,
        );
        onMovedToLedger?.();
        return;
      }
      if (isPayoutOnHold(error)) {
        const hold = refusedHoldOf(error) ?? (isRefusedHold(row.hold) ? row.hold : undefined);
        onRefused(
          t('payouts.record.refusedHold', "Can't be recorded while {{hold}}.", {
            hold: hold !== undefined ? holdLabel(hold) : t('payouts.record.holdUnknown', 'on hold'),
          }),
        );
        return;
      }
      if (isPayoutNotPayable(error)) {
        onRefused(
          t(
            'payouts.record.notPayable',
            "This settlement was charged to the reseller's card; there's nothing to pay.",
          ),
        );
        return;
      }
      setFormError(t('payouts.record.failed', 'Could not record the payout.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('payouts.record.title', 'Record payout')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {row.hold !== undefined && !isRefusedHold(row.hold) ? (
            <Alert severity="info">
              {t('payouts.record.holdInfo', 'On hold ({{hold}}); it can still be recorded.', {
                hold: holdLabel(row.hold),
              })}
            </Alert>
          ) : null}
          {formError ? <Alert severity="error">{formError}</Alert> : null}
          <TextField
            label={t('payouts.record.amount', 'Amount')}
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            slotProps={{ htmlInput: { inputMode: 'decimal' } }}
          />
          <TextField
            label={t('payouts.record.paidAt', 'Paid at')}
            type="date"
            value={paidAt}
            onChange={(event) => setPaidAt(event.target.value)}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            label={t('payouts.record.ref', 'Reference')}
            value={ref}
            onChange={(event) => setRef(event.target.value)}
            required
            error={Boolean(fieldErrors.ref)}
            helperText={fieldErrors.ref}
          />
          <Box>
            <AppSwitch
              label={t('payouts.record.override', 'Record a different amount')}
              checked={override}
              onChange={setOverride}
            />
            <Typography variant="caption" color="text.secondary" component="p">
              {t(
                'payouts.record.overrideHelp',
                'Only when the amount paid differs from the payable amount; give a reason.',
              )}
            </Typography>
          </Box>
          {override ? (
            <TextField
              label={t('payouts.record.reason', 'Reason')}
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              required
              error={Boolean(fieldErrors.reason)}
              helperText={fieldErrors.reason}
              multiline
            />
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('payouts.record.cancel', 'Cancel')}</Button>
        <SubmitButton onClick={() => void submit()} busy={submitting}>
          {t('payouts.record.submit', 'Record')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}
